import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, ArrowLeft, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import ThemeToggle from '../components/ThemeToggle';
import { useAuthStore } from '../store/auth-store';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface GiftDetails {
  plan: string;
  creatorName: string;
  isValid: boolean;
  error?: string;
}

const RedeemPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [giftDetails, setGiftDetails] = useState<GiftDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const code = searchParams.get('code');
    if (code) {
      checkGiftCode(code);
    }
  }, [user, searchParams, navigate]);

  const checkGiftCode = async (giftCode: string) => {
    setIsChecking(true);
    try {
      const { data: gift, error } = await supabase
        .from('gift_codes')
        .select(`
          *,
          creator:creator_id (
            raw_user_meta_data->name
          )
        `)
        .eq('code', giftCode)
        .single();

      if (error) throw error;

      // Call the validation function
      const { data, error: validationError } = await supabase
        .rpc('validate_gift_code', {
          code_to_check: giftCode,
          user_to_check: user?.id
        });

      if (validationError) {
        setGiftDetails({
          plan: gift.plan,
          creatorName: gift.creator?.name || 'Unknown',
          isValid: false,
          error: validationError.message
        });
        return;
      }

      setGiftDetails({
        plan: gift.plan,
        creatorName: gift.creator?.name || 'Unknown',
        isValid: true
      });
    } catch (error: any) {
      setGiftDetails(null);
      toast.error('Invalid gift code');
    } finally {
      setIsChecking(false);
    }
  };

  const handleRedeem = async () => {
    if (!code.trim() || !user || !giftDetails?.isValid) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .rpc('redeem_gift_code', {
          code_to_redeem: code,
          user_id: user.id
        });

      if (error) throw error;

      toast.success('Gift code redeemed successfully!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <div className="fixed top-0 left-0 right-0 border-b bg-background/80 backdrop-blur-sm z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <ThemeToggle />
        </div>
      </div>

      <div className="container mx-auto px-4 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="relative overflow-hidden rounded-2xl border bg-card/50 backdrop-blur-sm p-8 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-primary/10 opacity-10" />
            
            <div className="relative text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center"
              >
                <Gift className="w-8 h-8 text-primary" />
              </motion.div>

              <h1 className="text-2xl font-bold mb-2">
                Redeem Gift Code
              </h1>
              
              {giftDetails ? (
                <div className="mb-6">
                  <p className="text-muted-foreground">
                    Gift from <span className="font-medium text-foreground">{giftDetails.creatorName}</span>
                  </p>
                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    CodeNest {giftDetails.plan.charAt(0).toUpperCase() + giftDetails.plan.slice(1)}
                  </div>
                  {giftDetails.error && (
                    <p className="mt-2 text-sm text-destructive">{giftDetails.error}</p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground mb-6">
                  Enter your gift code below to redeem your subscription
                </p>
              )}

              <div className="space-y-4">
                <Input
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (e.target.value.length >= 8) {
                      checkGiftCode(e.target.value);
                    } else {
                      setGiftDetails(null);
                    }
                  }}
                  placeholder="Enter gift code"
                  className="text-center"
                  disabled={isLoading}
                />

                <Button
                  onClick={handleRedeem}
                  disabled={isLoading || isChecking || !giftDetails?.isValid}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redeeming...
                    </>
                  ) : isChecking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'Redeem Gift'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RedeemPage;